import { useEffect, useRef, useState } from "react";

/** Uses the page's persisted preferences; the menu never owns a second copy. */
export function ViewingOptions({ speeds, traces, auto, onRender, onAuto }: {
  speeds: boolean; traces: boolean; auto: boolean;
  onRender: (value: { speeds: boolean; traces: boolean }) => void; onAuto: () => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const gear = useRef<HTMLButtonElement>(null);
  const close = () => { setOpen(false); gear.current?.focus({ preventScroll: true }); };
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) close(); };
    const escape = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape, true); };
  }, [open]);
  return <div ref={box} className={`timeline-options ${open ? "open" : ""}`}>
    {open && <div className="timeline-option-list" role="group" aria-label="Viewing options">
      {([
        ["speeds", "Show speeds", speeds, () => onRender({ speeds: !speeds, traces })],
        ["traces", "Show traces", traces, () => onRender({ speeds, traces: !traces })],
        ["next", "Autoplay", auto, onAuto],
      ] as const).map(([id, label, checked, change]) => <button key={id} role="switch" aria-checked={checked} aria-label={label} data-testid={`opt-${id}`} onClick={change}>
        <OptionIcon kind={id} /><span>{label}</span><small>{checked ? "On" : "Off"}</small>
      </button>)}
    </div>}
    <button ref={gear} className="timeline-gear" aria-label={open ? "Close viewing options" : "Viewing options"} aria-expanded={open} onClick={() => setOpen(!open)} data-testid="viewing-options"><OptionIcon kind={open ? "close" : "gear"} /></button>
  </div>;
}

function OptionIcon({ kind }: { kind: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">{kind === "close" ? <path d="m6 6 12 12M6 18 18 6" />
    : kind === "speeds" ? <><path d="M4 17a9 9 0 1 1 16 0M12 13l5-5" /><circle cx="12" cy="13" r="2" /></>
    : kind === "traces" ? <><path d="M3 19c4-1 5-10 10-13s6 0 8 2M3 22h18" /><circle cx="19" cy="8" r="2" /></>
    : kind === "next" ? <><path d="m6 4 12 8-12 8zM21 4v16" /></>
    : <><path d="m9 3-1 3-3 1 1 3-2 2 2 2-1 3 3 1 1 3h6l1-3 3-1-1-3 2-2-2-2 1-3-3-1-1-3z" /><circle cx="12" cy="12" r="3" /></>}</svg>;
}
